import {
	DocumentStatusesEnum,
	PaymentStatusesEnum,
	FundOrderStatusesEnum,
	LinkedAccountStatusesEnum,
	IndividualExtendedModel,
	FundProductPricesEnum,
	FundProductStatusesEnum,
	ProductGroup,
	ProductSubType,
	ProductCategory,
	SettlementType,
	BarrierType,
	WarrantType,
	CouponFrequency,
	FundOrderTypesEnum, FundProductTypesEnum, AdjustmentCategoryEnum,
} from '@spa/api';

import { Language } from '../../language';
import {LanguageService} from "../../language.service";

export class TextsHelper {
  private constructor() {
  }

  static getPriceTypeName(priceType: number, language?: Language): string {
    const isBulgarian = language === Language.Bulgarian;

    switch (priceType) {
      case FundProductPricesEnum.IssuePrice:
        return isBulgarian ? 'емисионна стойност' : 'Issue Price';
      case FundProductPricesEnum.BidPrice:
        return isBulgarian ? 'продажна цена' : 'Sell Price';
      case FundProductPricesEnum.PenalRedemptionPrice:
        return isBulgarian ? 'продажна цена (с такса)' : 'Penal Redemption Price';
      case FundProductPricesEnum.NetAssetValue:
        return isBulgarian ? 'нетна стойност на активите' : 'Net Asset Value';
      case FundProductPricesEnum.AskPrice:
        return isBulgarian ? 'покупна цена' : 'Purchase Price';
      default:
        return '';
    }
  }

	static getProductTypeOrSubTypeName(type: FundProductTypesEnum, subType: ProductSubType, language?: Language): string {
		const isBulgarian = language === Language.Bulgarian;

		switch (type) {
			case FundProductTypesEnum.Bonds:
				return TextsHelper.getProductSubTypeName(subType, language);
			case FundProductTypesEnum.MutualFund:
				return isBulgarian ? 'ДФ' : 'Mutual Funds';
			case FundProductTypesEnum.InvestmentCertificate:
				return isBulgarian ? 'Investment Certificates' : 'Investment Certificates';
			default:
				return '';
		}
	}

	static getProductTypeName(type: FundProductTypesEnum, language?: Language): string {
		const isBulgarian = language === Language.Bulgarian;

		switch (type) {
			case FundProductTypesEnum.Bonds:
				return isBulgarian ? 'ДЦК' : 'Government Bonds';
			case FundProductTypesEnum.MutualFund:
				return isBulgarian ? 'ДФ' : 'Mutual Funds';
			case FundProductTypesEnum.InvestmentCertificate:
				return isBulgarian ? 'Investment Certificates' : 'Investment Certificates';
			default:
				return '';
		}
	}

  static getProductStatusName(status: FundProductStatusesEnum, language?: Language): string {
    const isBulgarian = language === Language.Bulgarian;

    switch (status) {
      case FundProductStatusesEnum.InSubscription:
        return isBulgarian ? 'В процес на записване' : 'In Subscription';
      case FundProductStatusesEnum.Active:
        return isBulgarian ? 'Активна' : 'Active';
	  case FundProductStatusesEnum.Expired:
		return isBulgarian ? 'Просрочен' : 'Expired';
      default:
        return '';
    }
  }

  static getOrderStatusName(status: number, language?: Language): string {
    const isBulgarian = language === Language.Bulgarian;

    switch (status) {
      case FundOrderStatusesEnum.All:
      case FundOrderStatusesEnum.New:
      case FundOrderStatusesEnum.Matched:
	  case FundOrderStatusesEnum.Pending:
	  case FundOrderStatusesEnum.Problematic:
        return isBulgarian ? 'Прието' : 'Pending';
      case FundOrderStatusesEnum.Completed:
        return isBulgarian ? 'Изпълнено' : 'Completed';
      case FundOrderStatusesEnum.Rejected:
        return isBulgarian ? 'Отказано' : 'Rejected';
      default:
        return '';
    }
  }

  static getOrderTypeName(type: FundOrderTypesEnum, language?: Language): string {
    const isBulgarian = language === Language.Bulgarian;

    switch (type) {
      case FundOrderTypesEnum.Buy:
        return isBulgarian ? 'Покупка' : 'Buy';
      case FundOrderTypesEnum.Sell:
        return isBulgarian ? 'Продажба' : 'Sell';
	case FundOrderTypesEnum.Payout:
		return isBulgarian ? 'Изплащане' : 'Payout';
      default:
        return '';
    }
  }

  static getDocumentStatusName(status: number, language?: Language): string {
    const isBulgarian = language === Language.Bulgarian;

    switch (status) {
      case DocumentStatusesEnum.New:
        return isBulgarian ? 'За одобрение' : 'For approval';
      case DocumentStatusesEnum.InReview:
        return isBulgarian ? 'Разглежда се' : 'In review';
      case DocumentStatusesEnum.Approved:
        return isBulgarian ? 'Одобрен' : 'Approved';
      case DocumentStatusesEnum.Archived:
        return isBulgarian ? 'Архивиран' : 'Archived';
      case DocumentStatusesEnum.Expired:
        return isBulgarian ? 'Изтекъл' : 'Expired';
      case DocumentStatusesEnum.Declined:
        return isBulgarian ? 'Отказан' : 'Declined';
      default:
        return '';
    }
  }

  static getLinkedAccountStatusName(status: number, language?: Language): string {
    const isBulgarian = language === Language.Bulgarian;

    switch (status) {
      case LinkedAccountStatusesEnum.New:
        return isBulgarian ? 'За одобрение' : 'Pending';
      case LinkedAccountStatusesEnum.Active:
        return isBulgarian ? 'Активен' : 'Active';
      case LinkedAccountStatusesEnum.Inactive:
        return isBulgarian ? 'Неактивен' : 'Inactive';
      default:
        return '';
    }
  }

  static getExpirationDateHelpers(currentLanguage: string): [string, string] {
    if (currentLanguage === Language.English) {
      return ['Non-expiring', 'Expiring'];
    }

    return ['Безсрочен', 'С валидност'];
  }

  static getPaymentStatusName(status: number): string {
    switch (status) {
      case PaymentStatusesEnum.All:
        return 'All';
      case PaymentStatusesEnum.New:
      case PaymentStatusesEnum.Pending:
        return 'Pending';
      case PaymentStatusesEnum.Processed:
      case PaymentStatusesEnum.OnHold:
      case PaymentStatusesEnum.ForApproval:
      case PaymentStatusesEnum.Approved:
        return 'Processing';
      case PaymentStatusesEnum.Canceled:
        return 'Cancelled by user';
      case PaymentStatusesEnum.CanceledByClient:
        return 'Cancelled by client';
      case PaymentStatusesEnum.Completed:
        return 'Completed';
      case PaymentStatusesEnum.Rejected:
        return 'Rejected';
      default:
        return '';
    }
  }

  static getPaymentStatusTranslatedNameKey(status: number): string {
    switch (status) {
      case PaymentStatusesEnum.All:
        return 'PAYMENT_STATUS_ALL';
      case PaymentStatusesEnum.New:
      case PaymentStatusesEnum.Pending:
        return 'PAYMENT_STATUS_PENDING';
      case PaymentStatusesEnum.Processed:
      case PaymentStatusesEnum.OnHold:
      case PaymentStatusesEnum.ForApproval:
      case PaymentStatusesEnum.Approved:
        return 'PAYMENT_STATUS_PROCESSING';
      case PaymentStatusesEnum.Canceled:
        return 'PAYMENT_STATUS_CANCELED_BY_USER';
      case PaymentStatusesEnum.CanceledByClient:
        return 'PAYMENT_STATUS_CANCELED_BY_CLIENT';
      case PaymentStatusesEnum.Completed:
        return 'PAYMENT_STATUS_COMPLETED';
      case PaymentStatusesEnum.Rejected:
        return 'PAYMENT_STATUS_REJECTED';
      default:
        return '';
    }
  }

  static getIndividualGreeting(individual: IndividualExtendedModel, currentLanguage: string): string {
    if (currentLanguage === Language.English) {
      return `Dear ${individual.lastName},`;
    }

    return `Уважаеми ${individual.lastName},`;
  }

  static getInterestPaymentPeriodName(frequency: number, languageService: LanguageService): string {
    switch (frequency) {
      case CouponFrequency.All:
        return '';
      case CouponFrequency.Monthly:
        return languageService.getInstantTranslationByKey('DASHBOARD.PRODUCT_DETAILS_CARD_COUPON_FREQUENCY_MONTHLY');
      case CouponFrequency.Quarterly:
		  return languageService.getInstantTranslationByKey('DASHBOARD.PRODUCT_DETAILS_CARD_COUPON_FREQUENCY_QUARTERLY');
      case CouponFrequency.SemiAnually:
		  return languageService.getInstantTranslationByKey('DASHBOARD.PRODUCT_DETAILS_CARD_COUPON_FREQUENCY_SEMI_ANNUALLY');
      case CouponFrequency.Anually:
		  return languageService.getInstantTranslationByKey('DASHBOARD.PRODUCT_DETAILS_CARD_COUPON_FREQUENCY_ANNUALLY');
      default:
        return '';
    }
  }

  static getProductCategoryName(category: number): string {
    switch (category) {
      case ProductCategory.NotAvailable:
        return 'N/A';
      case ProductCategory.CapitalProtection:
        return 'Capital Protection';
      case ProductCategory.InvestmentProductswithadditionalcreditrisk:
        return 'Investment Products with Additional Credit Risk';
      case ProductCategory.Leverage:
        return 'Leverage';
      case ProductCategory.Participation:
        return 'Participation';
      case ProductCategory.YieldEnhancement:
        return 'Yield Enhancement';
	  case ProductCategory.FixedRateBonds:
		return 'Fixed Rate Bonds';
	  case ProductCategory.FloatingRateBonds:
		return 'Floating Rate Bonds';
	  case ProductCategory.ZeroCouponBonds:
		return 'Zero Coupon Bonds';
      default:
        return '';
    }
  }

  static getProductSubTypeName(type: number, language: string): string {
    switch (type) {
      case ProductSubType.NotAvailable:
        return 'N/A';
      case ProductSubType.BarrierDiscountCertificate:
        return 'Barrier Discount Certificate';
      case ProductSubType.MultiBarrierDiscountCertificate:
        return 'Multi Barrier Discount Certificate';
      case ProductSubType.AutocallableMultiBarrierReverseConvertible:
        return 'Autocallable Multi Barrier Reverse Convertible';
      case ProductSubType.BarrierReverseConvertible:
        return 'Barrier Reverse Convertible';
      case ProductSubType.CallableMultiBarrierReverseConvertible:
        return 'Callable Multi Barrier Reverse Convertible';
      case ProductSubType.CallableBarrierReverseConvertible:
        return 'Callable Barrier Reverse Convertible';
      case ProductSubType.LockInCertificate:
        return 'Lock-In Certificate';
      case ProductSubType.MultiBarrierReverseConvertible:
        return 'Multi Barrier Reverse Convertible';
      case ProductSubType.ConditionalCouponBarrierReverseConvertible:
        return 'Conditional Coupon Barrier Reverse Convertible';
      case ProductSubType.ConditionalCouponMultiBarrierReverseConvertible:
        return 'Conditional Coupon Multi Barrier Reverse Convertible';
      case ProductSubType.ConditionalCouponMultiReverseConvertible:
        return 'Conditional Coupon Multi Reverse Convertible';
      case ProductSubType.DiscountCertificate:
        return 'Discount Certificate';
      case ProductSubType.MultiExpressCertificatewithBarrier:
        return 'Multi Express Certificate with Barrier';
      case ProductSubType.MiscellaneousYieldEnhancementCertificates:
        return 'Miscellaneous Yield Enhancement Certificates';
      case ProductSubType.AutocallableMultiReverseConvertible:
        return 'Autocallable Multi Reverse Convertible';
      case ProductSubType.AutocallableReverseConvertible:
        return 'Autocallable Reverse Convertible';
      case ProductSubType.CallableMultiReverseConvertible:
        return 'Callable Multi Reverse Convertible';
      case ProductSubType.CallableReverseConvertible:
        return 'Callable Reverse Convertible';
      case ProductSubType.MultiReverseConvertible:
        return 'Multi Reverse Convertible';
      case ProductSubType.ReverseConvertible:
        return 'Reverse Convertible';
      case ProductSubType.BonusCertificate:
        return 'Bonus Certificate';
      case ProductSubType.CappedBonusCertificate:
        return 'Capped Bonus Certificate';
      case ProductSubType.CappedMultiBonusCertificate:
        return 'Capped Multi Bonus Certificate';
      case ProductSubType.CouponMultiBonusCertificate:
        return 'Coupon Multi Bonus Certificate';
      case ProductSubType.MultiBonusCertificate:
        return 'Multi Bonus Certificate';
      case ProductSubType.MultiBonusOutperformanceCertificate:
        return 'Multi Bonus Outperformance Certificate';
      case ProductSubType.MiscellaneousParticipationCertificate:
        return 'Miscellaneous Participation Certificate';
      case ProductSubType.CappedOutperformanceCertificate:
        return 'Capped Outperformance Certificate';
      case ProductSubType.OutperformanceCertificate:
        return 'Outperformance Certificate';
      case ProductSubType.ActivelyManagedCertificate:
        return 'Actively Managed Certificate';
      case ProductSubType.TrackerCertificate:
        return 'Tracker Certificate';
      case ProductSubType.MiniFuture:
        return 'Mini Future';
      case ProductSubType.Warrant:
        return 'Warrant';
      case ProductSubType.ReferenceEntityCertificatewithConditionalCapitalProtection:
        return 'Reference Entity Certificate with Conditional Capital Protection';
      case ProductSubType.CapitalProtectionCertificatewithBarrier:
        return 'Capital Protection Certificate with Barrier';
      case ProductSubType.AutocallablesCapitalProtectionCertificatewithCoupon:
        return 'Autocallables Capital Protection Certificate with Coupon';
      case ProductSubType.CapitalProtectionCertificatewithCoupon:
        return 'Capital Protection Certificate with Coupon';
      case ProductSubType.CapitalProtectedCertificatewithCap:
        return 'Capital Protected Certificate with Cap';
      case ProductSubType.CapitalProtectedCertificatewithoutCap:
        return 'Capital Protected Certificate without Cap';
      case ProductSubType.MiscellaneousCapitalProtectedCertificates:
        return 'Miscellaneous Capital Protected Certificates';
	  case ProductSubType.CorporateBonds:
		return language === Language.English ? 'Corporate Bonds' : 'Корпоративни облигации';
	  case ProductSubType.MunicipalBonds:
		return 'Municipal Bonds';
	  case ProductSubType.GovernmentBonds:
		return language === Language.English ? 'Government Bonds' : 'ДЦК';
      default:
        return '';
    }
  }

  static getProductGroupName(group: number): string {
    switch (group) {
      case ProductGroup.NotAvailable:
        return 'N/A';
      case ProductGroup.MiscellaneousCapitalProtectedCertificates:
        return 'Miscellaneous Capital Protected Certificates';
      case ProductGroup.CapitalProtectionCertificatewithParticipation:
        return 'Capital Protection Certificate With Participation';
      case ProductGroup.CapitalProtectionCertificatewithCoupon:
        return 'Capital Protection Certificate With Coupon';
      case ProductGroup.CapitalProtectionCertificatewithBarrier:
        return 'Capital Protection Certificate With Barrier';
      case ProductGroup.ReferenceEntityCertificatewithConditionalCapitalProtection:
        return 'Reference Entity Certificate With Conditional Capital Protection';
      case ProductGroup.Warrant:
        return 'Warrant';
      case ProductGroup.MiniFuture:
        return 'Mini Future';
      case ProductGroup.TrackerCertificate:
        return 'Tracker Certificate';
      case ProductGroup.OutperformanceCertificates:
        return 'Out Performance Certificates';
      case ProductGroup.MiscellaneousParticipationCertificate:
        return 'Miscellaneous Participation Certificate';
      case ProductGroup.BonusOutperformanceCertificate:
        return 'Bonus Out Performance Certificate';
      case ProductGroup.BonusCertificate:
        return 'Bonus Certificate';
      case ProductGroup.ReverseConvertible:
        return 'Reverse Convertible';
      case ProductGroup.MiscellaneousYieldEnhancementCertificates:
        return 'Miscellaneous Yield Enhancement Certificates';
      case ProductGroup.ExpressCertificates:
        return 'Express Certificates';
      case ProductGroup.DiscountCertificate:
        return 'Discount Certificate';
      case ProductGroup.ConditionalCouponReverseConvertible:
        return 'Conditional Coupon Reverse Convertible';
      case ProductGroup.ConditionalCouponBarrierReverseConvertible:
        return 'Conditional Coupon Barrier Reverse Convertible';
      case ProductGroup.BarrierReverseConvertible:
        return 'Barrier Reverse Convertible';
      case ProductGroup.BarrierDiscountCertificate:
        return 'Barrier Discount Certificate';
      default:
        return '';
    }
  }


  static getProductSettlementTypeName(type: number): string {
    switch (type) {
      case SettlementType.NotAvailable:
        return 'N/A';
      case SettlementType.CashSettlement:
        return 'Cash Settlement';
      case SettlementType.CashSettlementOrDelivery:
        return 'Cash Settlement or Delivery';
      default:
        return '';
    }
  }

  static getProductBarrierTypeName(type: number): string {
    switch (type) {
      case BarrierType.NotAvailable:
        return 'N/A';
      case BarrierType.BarrierObservationAtMaturityOnly:
        return 'Barrier Observation at Maturity Only';
      case BarrierType.ContinuousBarrierObservation:
        return 'Continuous Barrier Observation';
      case BarrierType.ContinuousBarrierObservationOnTheClose:
        return 'Continuous Barrier Observation on the Close';
      default:
        return '';
    }
  }

  static getProductWarrantTypeName(type: number): string {
    switch (type) {
      case WarrantType.NotAvailable:
        return 'N/A';
      case WarrantType.Call:
        return 'Call';
      case WarrantType.Put:
        return 'Put';
      default:
        return '';
    }
  }

	static getAdjustmentCategoryName(type: string): string {
		switch (type) {
			case AdjustmentCategoryEnum.StockLocate:
				return 'Stock Locate';
			case AdjustmentCategoryEnum.Software:
				return 'Software';
			case AdjustmentCategoryEnum.ShortMarginInterest:
				return 'Short Margin Interest';
			case AdjustmentCategoryEnum.Platform:
				return 'Platform';
			case AdjustmentCategoryEnum.PNL:
				return 'PNL';
			case AdjustmentCategoryEnum.OvernightBorrow:
				return 'Overnight Borrow';
			case AdjustmentCategoryEnum.Miscellaneous:
				return 'Miscellaneous';
			case AdjustmentCategoryEnum.MarketData:
				return 'Market Data';
			case AdjustmentCategoryEnum.Invoice:
				return 'Invoice';
			case AdjustmentCategoryEnum.Dividend:
				return 'Dividend';
			case AdjustmentCategoryEnum.eSignal:
				return 'eSignal';
			case AdjustmentCategoryEnum.Bundle:
				return 'Bundle';
			case AdjustmentCategoryEnum.Rebate:
				return 'Rebate';
		}

		return 'N/A';
	}
}
